import { toast, ToastOptions, ToastPosition } from "react-toastify";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheckOutlined } from "../../libs";
import { VeTypography } from "../../mui";

// Demo use-case for success
// ShowCustomToast({
//   data: {
//     title: "The Subtask has been added successfully.",
//     content: 'Click "View Task" to add more details.',
//   },
//   actionButtonText: "Add Task",
//   onAction: handleActionClick,
//   type: "success",
//   autoClose: 5000, // Optional
//   buttonPosition: "left", // Optional
// });

// Demo use-case for error
// ShowCustomToast({
//   data: {
//     title: "Subtask moved to trash.",
//   },
//   actionButtonText: "Undo",
//   onAction: handleActionClick,
//   type: "error",
// });

const successColor = "#25855A";
const errorColor = "#8F9295";
const loadingColor = "#886BE8";
interface ToastData {
  title: string; // Main title of the toast
  content?: string; // Optional content for additional description
}

type options = "success" | "error" | "loading" | "info" | "warning";
type ButtonPosition = "left" | "right";

interface CustomToastProps {
  data: ToastData; // Data for the toast (title, content)
  actionButtonText?: string; // Optional action button text
  onAction?: () => void; // Action button handler
  type?: options; // Icon or ReactNode
  position?: ToastPosition; //for positioning the toast
  autoClose?: number | false; // Auto-close duration in milliseconds (default: false)
  buttonPosition?: ButtonPosition;
  pauseOnHover?: boolean; // Disable autoClose if mouse is hovered.
}

export const ShowCustomToast = (
  {
    data,
    actionButtonText,
    onAction,
    type = "success",
    position = "top-right",
    autoClose = false,
    buttonPosition = "left",
    pauseOnHover = true,
  }: CustomToastProps,
  options?: ToastOptions,
) => {
  const closeToast = () => {
    toast.dismiss();
  };

  const mainColor =
    type === "success"
      ? successColor
      : type === "loading"
        ? loadingColor
        : errorColor;

  const toastContent1 = (
    <Box sx={{ borderLeft: `5px solid ${mainColor}`, padding: "8px" }}>
      {/* Icon and Message */}
      <Box
        display="flex"
        alignItems={actionButtonText ? "start" : "center"}
        gap={2}
      >
        <Box display={"flex"} alignItems="center">
          <FontAwesomeIcon
            icon={faCircleCheckOutlined}
            color={successColor}
            style={{ width: "18px", height: "18px" }}
          />
        </Box>
        <Box
          flex="1"
          display={"flex"}
          flexDirection={"column"}
          marginBottom={actionButtonText ? "16px" : "0px"}
        >
          <VeTypography variant="label2StrongMain">{data.title}</VeTypography>
          {data.content && (
            <VeTypography variant="label3Tertiary">{data.content}</VeTypography>
          )}
        </Box>
        {/* Close Icon */}

        <CloseIcon
          onClick={closeToast}
          style={{
            fill: "#5F6368",
            height: "10px",
            width: "10px",
            marginTop: actionButtonText ? "5px" : "2px",
          }}
          className="clear-icon"
        />
      </Box>
      {/* Action Button */}
      {actionButtonText && onAction && (
        <Box
          display="flex"
          justifyContent={buttonPosition === "left" ? "start" : "end"}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              onAction();
              closeToast();
            }}
            sx={{
              padding: "6px 12px",
              lineHeight: 1,
              height: "auto",
              textTransform: "none",
              whiteSpace: "nowrap",
              marginRight: "12px",
              marginLeft: "34px",
              "&:hover": {
                borderColor: mainColor,
                color: mainColor,
              },
            }}
          >
            <Typography variant="label3StrongTertiary">
              {actionButtonText}
            </Typography>
          </Button>
        </Box>
      )}
    </Box>
  );

  const toastContent2 = (
    <Box
      sx={{
        borderLeft: `5px solid ${mainColor}`,
        padding: "8px",
        paddingLeft: "12px",
      }}
    >
      {/* Icon and Message */}
      <Box display="flex" alignItems="center" gap={2}>
        {/* <Box
          sx={{
            width: "10px",
            height: "10px",
            borderRadius: "2px",
            background: errorColor,
          }}
        ></Box> */}
        <Box flex="1">
          <VeTypography variant="label2StrongMain">{data.title}</VeTypography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          {/* Action Button */}
          {actionButtonText && onAction && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                onAction();
                closeToast();
              }}
              sx={{
                padding: "6px 12px",
                lineHeight: 1,
                height: "auto",
                textTransform: "none",
                whiteSpace: "nowrap",
                "&:hover": {
                  borderColor: mainColor,
                  color: mainColor,
                },
              }}
            >
              <Typography variant="label3StrongTertiary">
                {actionButtonText}
              </Typography>
            </Button>
          )}
          {/* Close Icon */}
          <CloseIcon
            onClick={closeToast}
            style={{
              fill: "#5F6368",
              height: "10px",
              width: "10px",
              marginTop: "4px",
            }}
            className="clear-icon"
          />
        </Box>
      </Box>
    </Box>
  );

  const toastContent3 = (
    <Box
      sx={{
        borderLeft: `5px solid ${mainColor}`,
        padding: "8px",
        paddingLeft: "12px",
      }}
    >
      {/* Icon and Message */}
      <Box display="flex" alignItems="center" gap={2}>
        {/* <Box
          sx={{
            width: "10px",
            height: "10px",
            borderRadius: "2px",
            background: errorColor,
          }}
        ></Box> */}
        <CircularProgress size={16} sx={{ color: loadingColor }} />
        <Box flex="1">
          <VeTypography variant="label2StrongMain">{data.title}</VeTypography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          {/* Action Button */}
          {actionButtonText && onAction && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                onAction();
                closeToast();
              }}
              sx={{
                padding: "6px 12px",
                lineHeight: 1,
                height: "auto",
                textTransform: "none",
                whiteSpace: "nowrap",
                "&:hover": {
                  borderColor: mainColor,
                  color: mainColor,
                },
              }}
            >
              <Typography variant="label3StrongTertiary">
                {actionButtonText}
              </Typography>
            </Button>
          )}
          {/* Close Icon */}
          <CloseIcon
            onClick={closeToast}
            style={{
              fill: "#5F6368",
              height: "10px",
              width: "10px",
              marginTop: "4px",
            }}
            className="clear-icon"
          />
        </Box>
      </Box>
    </Box>
  );

  toast(
    type === "success"
      ? toastContent1
      : type === "loading"
        ? toastContent3
        : toastContent2,
    {
      position,
      autoClose,
      // autoClose: 3000,
      closeButton: false,
      hideProgressBar: true,
      draggable: true,
      pauseOnHover: pauseOnHover,
      ...options,
    },
  );
};

const CloseIcon = ({ onClick, style, className }: any) => {
  return (
    <svg
      style={{ cursor: "pointer", ...style }}
      onClick={onClick}
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M13.295 2.115C13.6844 1.72564 13.6844 1.09436 13.295 0.705C12.9056 0.315639 12.2744 0.315639 11.885 0.705L7 5.59L2.115 0.705C1.72564 0.315639 1.09436 0.315639 0.705 0.705C0.315639 1.09436 0.315639 1.72564 0.705 2.115L5.59 7L0.705 11.885C0.315639 12.2744 0.315639 12.9056 0.705 13.295C1.09436 13.6844 1.72564 13.6844 2.115 13.295L7 8.41L11.885 13.295C12.2744 13.6844 12.9056 13.6844 13.295 13.295C13.6844 12.9056 13.6844 12.2744 13.295 11.885L8.41 7L13.295 2.115Z"
        fill="#5F6368"
      />
    </svg>
  );
};
